import React from "react";
import { createPortal } from "react-dom";
import type { PreviewTrack } from "../types/setlists";
import { artistNames, type SetlistReview } from "../hooks/useSetlistReview";
import { t as tx } from "../i18n";
import "./SetlistTrackReview.css";

// The tracklist review both Sell Playlists uploads share (founder, 10/10):
// every song shown with a one-tap ✕ (keep your secret weapons), Confirm or
// Swap on the rows we weren't sure of, and + to add songs the file doesn't
// have. State lives in useSetlistReview; the parent owns the frame around it.

const Art: React.FC<{ src?: string | null }> = ({ src }) =>
  src ? (
    <img className="suf-row-art" src={src} alt="" loading="lazy" />
  ) : (
    <div className="suf-row-art suf-row-art--none" aria-hidden>
      ?
    </div>
  );

const SetlistTrackReview: React.FC<{ review: SetlistReview }> = ({ review }) => {
  const { preview, flagged, autoMatched, unresolvedFlagged, decisions, added, finalTracks, removedCount, canAdd, picker } = review;
  if (!preview) return null;

  const flaggedRow = (t: PreviewTrack) => {
    const d = decisions[t.order];
    const shown = d?.kind === "swapped" ? `${d.track.name} — ${d.track.artists}` : t.match ? `${t.match.name} — ${artistNames(t.match)}` : null;
    return (
      <div className={`suf-row${d ? ` suf-row--${d.kind}` : ""}`} key={t.order}>
        <Art src={d?.kind === "swapped" ? d.track.image : t.match?.image} />
        <div className="suf-row-meta">
          <div className="suf-row-file" title={`${t.title} — ${t.artist}`}>
            {t.title} — {t.artist}
          </div>
          <div className={`suf-row-match${shown ? "" : " suf-row-match--none"}`} title={shown || tx("setlistUploadFlow.noMatchFound")}>
            {shown ? `→ ${shown}` : tx("setlistReview.notOnSpotify")}
          </div>
        </div>
        {d ? (
          <button type="button" className="suf-row-state" onClick={() => review.clearDecision(t.order)} title={tx("setlistUploadFlow.undo")} aria-label={tx("setlistUploadFlow.undo")}>
            {d.kind === "kept" && "✓"}
            {d.kind === "dropped" && "✕"}
            {d.kind === "swapped" && "⇄"}
          </button>
        ) : (
          <div className="suf-row-actions">
            {t.match && (
              <button type="button" className="suf-row-btn suf-row-btn--keep" onClick={() => review.setDecision(t.order, { kind: "kept" })} title={tx("setlistReview.confirm")} aria-label={tx("setlistReview.confirm")}>
                ✓
              </button>
            )}
            <button type="button" className="suf-row-btn" onClick={() => review.openSwap(t)} title={tx("setlistReview.swap")} aria-label={tx("setlistReview.swap")}>
              ⇄
            </button>
          </div>
        )}
      </div>
    );
  };

  // A confident match: ✕ removes it, ↩ brings it back. Tapping the song
  // itself still opens Swap, for the rare wrong "confident" match.
  const autoRow = (t: PreviewTrack) => {
    const d = decisions[t.order];
    const dropped = d?.kind === "dropped";
    const title = d?.kind === "swapped" ? d.track.name : t.match?.name || t.title;
    const artist = d?.kind === "swapped" ? d.track.artists : artistNames(t.match) || t.artist;
    return (
      <div className={`suf-row suf-row--auto${dropped ? " suf-row--dropped" : ""}${d?.kind === "swapped" ? " suf-row--swapped" : ""}`} key={t.order}>
        <Art src={d?.kind === "swapped" ? d.track.image : t.match?.image} />
        <button type="button" className="suf-row-meta srv-meta-btn" onClick={() => !dropped && review.openSwap(t)} disabled={dropped}>
          <span className="suf-row-file" title={title}>
            {title}
          </span>
          <span className="suf-row-match srv-artist" title={artist}>
            {artist}
          </span>
        </button>
        {dropped ? (
          <button type="button" className="suf-row-btn" onClick={() => review.clearDecision(t.order)} title={tx("setlistUploadFlow.restore")} aria-label={tx("setlistUploadFlow.restore")}>
            ↩
          </button>
        ) : (
          <button type="button" className="suf-row-btn srv-x" onClick={() => review.setDecision(t.order, { kind: "dropped" })} title={tx("setlistReview.remove")} aria-label={tx("setlistReview.remove")}>
            ✕
          </button>
        )}
      </div>
    );
  };

  const summary = [
    removedCount > 0 ? tx("setlistReview.removed", { count: removedCount }) : null,
    added.length > 0 ? tx("setlistReview.added", { count: added.length }) : null,
  ].filter(Boolean);

  return (
    <>
      <div className="srv-hint">{tx("setlistReview.tapToRemove")}</div>
      <div className="suf-review-scroll">
        {flagged.length > 0 && (
          <>
            <div className={`suf-flag-head${unresolvedFlagged.length > 0 ? "" : " suf-flag-head--done"}`}>
              {unresolvedFlagged.length > 0 ? tx("setlistReview.toReview", { count: unresolvedFlagged.length }) : tx("setlistUploadFlow.allReviewed")}
            </div>
            {unresolvedFlagged.map(flaggedRow)}
            {flagged.filter((t) => decisions[t.order]).map(flaggedRow)}
          </>
        )}

        {autoMatched.map(autoRow)}

        {added.map((s) => (
          <div className="suf-row suf-row--auto suf-row--kept" key={s.id}>
            <Art src={s.image} />
            <div className="suf-row-meta">
              <div className="suf-row-file" title={s.name}>
                {s.name}
              </div>
              <div className="suf-row-match srv-artist" title={s.artists}>
                {s.artists}
              </div>
            </div>
            <button type="button" className="suf-row-btn srv-x" onClick={() => review.removeAdded(s.id)} title={tx("setlistReview.remove")} aria-label={tx("setlistReview.remove")}>
              ✕
            </button>
          </div>
        ))}

        {canAdd && (
          <button type="button" className="srv-add" onClick={review.openAdd}>
            {tx("setlistReview.addASong")}
          </button>
        )}

        {(preview.excluded || 0) > 0 && <div className="suf-excluded">{tx("setlistUploadFlow.warmUpTracksSkipped", { count: preview.excluded || 0 })}</div>}
      </div>

      {summary.length > 0 && (
        <div className="srv-summary" role="status">
          {[...summary, tx("setlistReview.fansGet", { count: finalTracks.length })].join(" · ")}
        </div>
      )}

      {/* ── Search sheet: swap one row, or add a song ── */}
      {picker &&
        createPortal(
          <div className="suf-swap-overlay" onClick={review.closePicker}>
            <div className="suf-swap-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="srv-picker-title">
              <div className="suf-swap-title" id="srv-picker-title">{picker.mode === "swap" ? tx("setlistUploadFlow.swapTheMatch") : tx("setlistReview.addASongTitle")}</div>
              {picker.mode === "swap" && (
                <div className="suf-swap-for" title={`${picker.track.title} — ${picker.track.artist}`}>
                  {picker.track.title} — {picker.track.artist}
                </div>
              )}
              <input
                className="suf-swap-input"
                value={review.query}
                onChange={(e) => review.setQuery(e.target.value)}
                placeholder={tx("setlistUploadFlow.searchASong")}
                autoFocus
              />
              {review.searchError && <div className="srv-error">{review.searchError}</div>}
              <div className="suf-swap-results">
                {review.results.map((hit) => (
                  <button type="button" className="suf-swap-row" key={hit.id} onClick={() => review.pick(hit)}>
                    <img src={hit.image} alt="" loading="lazy" />
                    <span className="suf-swap-row-meta">
                      <span className="suf-swap-row-t">{hit.name}</span>
                      <span className="suf-swap-row-a">{hit.artists.map((a) => a.name).join(", ")}</span>
                    </span>
                  </button>
                ))}
                {review.noResults && <div className="srv-empty">{tx("setlistReview.noSongsFound")}</div>}
              </div>
              {picker.mode === "swap" && (
                <button type="button" className="suf-swap-drop" onClick={review.dropPickerTrack}>
                  {tx("setlistUploadFlow.removeThisTrackFrom")}
                </button>
              )}
              <button type="button" className="suf-swap-cancel" onClick={review.closePicker}>
                {tx("setlistUploadFlow.cancel")}
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default SetlistTrackReview;
